import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ColumnDef, Table } from "@tanstack/react-table";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";

interface ExpandingColumnOptions {
  showHeader?: boolean;
}

export const createExpandingColumn = <TData,>(
  options: ExpandingColumnOptions = {},
): ColumnDef<TData, unknown> => {
  const { showHeader = false } = options;

  return {
    id: "expand",
    enablePinning: true,
    header: showHeader
      ? ({ table }: { table: Table<TData> }) => {
          const rows = table.getRowModel().rows;
          const expandableRows = rows.filter((row) => row.getCanExpand());
          const expandedRows = expandableRows.filter((row) =>
            row.getIsExpanded(),
          );

          const allExpanded =
            expandableRows.length > 0 &&
            expandedRows.length === expandableRows.length;
          const someExpanded =
            expandedRows.length > 0 &&
            expandedRows.length < expandableRows.length;

          const handleExpandAll = () => {
            if (allExpanded) {
              // Collapse all
              table.toggleAllRowsExpanded(false);
            } else {
              // Expand all
              table.toggleAllRowsExpanded(true);
            }
          };

          if (expandableRows.length === 0) {
            return null;
          }

          return (
            <Stack
              direction="row"
              justify="center"
              align="center"
              className="min-h-[4.5rem] pl-2"
              fullWidth
              onClick={handleExpandAll}
            >
              <Button
                variant="icon"
                size="iconSm"
                className="bg-transparent"
                title={
                  allExpanded
                    ? "Collapse all"
                    : someExpanded
                      ? "Expand all remaining"
                      : "Expand all"
                }
              >
                {allExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </Button>
            </Stack>
          );
        }
      : () => null,
    cell: ({ row }) => {
      if (!row.getCanExpand()) {
        return null;
      }

      return (
        <Stack
          direction="row"
          justify="center"
          align="center"
          className="min-h-[4.5rem] pl-2"
          fullWidth
        >
          <Button
            variant="icon"
            size="iconSm"
            className="bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
          >
            {row.getIsExpanded() ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </Button>
        </Stack>
      );
    },
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
  };
};
